import { _ } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { AuthoringElementType } from "common/constants";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { AuthoringShapeElement } from "js/elements/elements/authoring/AuthoringShape";
import { AuthoringContentElement } from "js/elements/elements/authoring/AuthoringContent";
import { AuthoringPathElement } from "js/elements/elements/authoring/AuthoringPath";
import ElementManager from "js/elements/ElementManager";
import { Header } from "js/elements/elements/Header";
import { Footer } from "js/elements/elements/Footer";
import { AuthoringSvgElement } from "js/elements/elements/authoring/AuthoringSvg";
import { AuthoringRadialBar } from "js/elements/elements/authoring/AuthoringRadialBar";
import { AuthoringVideo } from "./authoring/AuthoringVideo";

export class AuthoringCanvas extends CollectionElement {
    static get schema() {
        return {
            snapToGrid: false,
            elements: [],
        };
    }

    get collectionPropertyName() {
        return "elements";
    }

    get minItemCount() {
        return 0;
    }

    get snapToGrid() {
        return !!this.model.snapToGrid;
    }

    get gridSpacing() {
        return 20;
    }

    get padding() {
        return 40;
    }

    get canRefreshElement() {
        return true;
    }

    refreshElement(transition, requireFit = false) {
        this.canvas.refreshElement(this, transition, true, requireFit);
    }

    getDefaultElementSize(elementType) {
        return new geom.Size(200, 200);
    }

    getCanvasMargins() {
        return { left: 0, top: 0, right: 0, bottom: 0 };
    }

    getChildItemType() {
        return AuthoringElementContainer;
    }

    _calcProps(props, options) {
        const { size, children } = props;

        this.itemElements.forEach((elementContainer, elementIndex) => {
            const { x, y, width, height } = elementContainer.model;

            const sizeForCalcProps = new geom.Size(width, height);
            if (elementContainer.element.fitToContents?.width && elementContainer.element.needsFullSizeToCalcFit?.width) {
                sizeForCalcProps.width = size.width;
            }
            if (elementContainer.element.fitToContents?.height && elementContainer.element.needsFullSizeToCalcFit?.height) {
                sizeForCalcProps.height = size.height;
            }

            const elementProps = children.setProps(elementContainer, sizeForCalcProps);

            if (elementContainer.element.fitToContents?.width) {
                if (elementContainer.element.fitToContentsAnchor && elementContainer.element.fitToContentsAnchor.width === geom.AnchorType.RIGHT) {
                    const widthDiff = elementContainer.model.width - elementProps.size.width;
                    elementContainer.model.x += widthDiff;
                }

                elementContainer.model.width = elementProps.size.width;
            }
            if (elementContainer.element.fitToContents?.height) {
                if (elementContainer.element.fitToContentsAnchor && elementContainer.element.fitToContentsAnchor.height === geom.AnchorType.BOTTOM) {
                    const heightDiff = elementContainer.model.height - elementProps.size.height;
                    elementContainer.model.y += heightDiff;
                }

                elementContainer.model.height = elementProps.size.height;
            }

            elementProps.bounds = new geom.Rect(x, y, elementProps.size);
            elementProps.layer = elementIndex;
        });

        return { size };
    }

    registerAnimations(animationManager) {
        animationManager.showAnimationProperties = false;
    }
}

class AuthoringElementContainer extends CollectionItemElement {
    static get schema() {
        return {
            isLocked: false,
            shadow: {},
            opacity: 100
        };
    }

    get isAuthoringElement() {
        return true;
    }

    get isEditable() {
        return !!this.element.isEditable;
    }

    get allowDecorationStyles() {
        return false;
    }

    get lockAspectRatio() {
        return this.element.lockAspectRatio ?? false;
    }

    get preserveAspectRatioOnCornerResize() {
        return this.element.preserveAspectRatioOnCornerResize ?? false;
    }

    get groupId() {
        if (this.model.groupIds.length === 0) {
            return null;
        }

        return this.model.groupIds[0];
    }

    set groupId(value) {
        if (value) {
            this.model.groupIds.unshift(value);
        } else {
            this.model.groupIds.shift();
        }
    }

    get isLocked() {
        return !!this.model.isLocked;
    }

    get canChangeDirection() {
        return !!this.element.canChangeDirection;
    }

    onResize(boundsBeforeResize) {
        if (this.element.onResize) {
            return this.element.onResize(boundsBeforeResize);
        }
    }

    getSelectionElement() {
        return this.element;
    }

    getElementFactory() {
        const elementType = this.model.type;

        switch (elementType) {
            case AuthoringElementType.SHAPE:
                return () => AuthoringShapeElement;
            case AuthoringElementType.PATH:
                return () => AuthoringPathElement;
            case AuthoringElementType.CONTENT:
                return () => AuthoringContentElement;
            case AuthoringElementType.COMPONENT:
                if (this.model.componentType) {
                    return () => ElementManager.get(this.model.componentType);
                } else {
                    return () => AuthoringShapeElement;
                }
            case AuthoringElementType.HEADER:
                return () => Header;
            case AuthoringElementType.FOOTER:
                return () => Footer;
            case AuthoringElementType.SVG:
                return () => AuthoringSvgElement;
            case AuthoringElementType.RADIAL_BAR:
                return () => AuthoringRadialBar;
            case AuthoringElementType.VIDEO:
                return () => AuthoringVideo;
        }
    }

    _build() {
        if (!this.model.groupIds) {
            this.model.groupIds = [];
        }

        this.element = this.addElement("element", this.getElementFactory(), {
            // NOTE: this.model.element may be undefined, in this case element and container will be sharing model
            model: this.model.element,
            isOnAuthoringCanvas: true
        });
    }

    getHTMLFilter() {
        switch (this.model.shadow) {
            case "drop":
                return "drop-shadow(3px 5px 6px rgba(0,0,0,.4)";
            case "soft":
                return "drop-shadow(0px 0px 10px rgba(0,0,0,.4)";
            case "block":
                return "drop-shadow(10px 10px 0px rgba(0,0,0,.5";
            default:
                if (this.model.shadow.hasOwnProperty("color") && this.model.shadow.opacity > 0) {
                    return `drop-shadow(${this.model.shadow.x}px ${this.model.shadow.y}px ${this.model.shadow.blur}px ${this.model.shadow.color})`;
                }
        }
    }

    _calcProps(props, options) {
        const { size, children, styles } = props;

        const elementProps = children.setProps(this.element, size);
        elementProps.bounds = new geom.Rect(0, 0, elementProps.size);

        return {
            size: elementProps.bounds.size,
            opacity: _.defaultTo(this.model.opacity, 100) / 100,
        };
    }
}

export const elements = {
    AuthoringCanvas
};

